import { useEffect, useRef } from 'react';

interface UseInfiniteScrollQueryProps {
  queries: { fetchMore: (options: any) => void; nextToken?: string }[];
  rootRef: React.RefObject<HTMLDivElement | null>;
  loading: boolean;
}

export const useInfiniteScrollQuery = ({ queries, rootRef, loading }: UseInfiniteScrollQueryProps) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!sentinelRef.current || !rootRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting)
          queries.forEach(({ nextToken, fetchMore }) => {
            if (!nextToken) return;
            if (nextToken && !loading)
              fetchMore({
                variables: { nextToken },
                updateQuery: (prev: any, { fetchMoreResult }: { fetchMoreResult?: any }) => {
                  if (!fetchMoreResult) return prev;
                  const merged: any = { ...prev };
                  for (const key of Object.keys(fetchMoreResult)) {
                    const prevValue = prev[key];
                    const newValue = fetchMoreResult[key];
                    if (prevValue?.items && newValue?.items)
                      merged[key] = {
                        ...newValue,
                        items: [...prevValue.items, ...newValue.items]
                      };
                    else merged[key] = newValue;
                  }
                  return merged;
                }
              });
          });
      },
      {
        root: rootRef.current,
        threshold: 0.2
      }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [queries, rootRef, loading]);

  return { sentinelRef };
};
